import { BitmapCoordinatesRenderingScope, CanvasRenderingTarget2D } from 'fancy-canvas'
import DrawingBase from '../drawing.base'
import {
    ColorValue,
    DrawingSettingGroup,
    DrawingTypes,
    LabelAxisViewConfig,
    Point,
    PointCoordinates,
    SettingValues
} from '../drawing.interface'
import { calculateTriangleArea, scaledCoordinate } from '../drawing.helper'
import { Coordinate, PrimitiveHoveredItem, Time } from 'lightweight-charts'
import { SettingType } from '../drawing.enums'
import { primaryDrawingColor } from '../../../../assets/colors'

export type TriangleDrawingState = {
    p1?: Point,
    p2?: Point,
    p3?: Point
}

export type TriangleDrawingSettings = {
    fillColor: ColorValue,
    lineColor: ColorValue,
    lineWidth: number,
    labelColor: ColorValue
}

class TriangleDrawingTool extends DrawingBase<any, TriangleDrawingSettings, TriangleDrawingState> {

    area: number = 0
    scaledCoordinates = {
        p1x: 0,
        p2x: 0,
        p1y: 0,
        p2y: 0,
        p3x: 0,
        p3y: 0
    }

    draw(scope: BitmapCoordinatesRenderingScope, target: CanvasRenderingTarget2D) {
        const ctx = scope.context
        this.calculateAttributes()
        let { p1x, p2x, p1y, p2y, p3x, p3y } = this.scaledCoordinates
        if (!p1x || !p2x || !p1y || !p2y) return

        ctx.globalAlpha = this._settings.lineColor.opacity
        ctx.strokeStyle = this._settings.lineColor.color
        ctx.lineWidth = this._settings.lineWidth * window.devicePixelRatio

        ctx.beginPath()

        ctx.moveTo(p1x, p1y)
        ctx.lineTo(p2x, p2y)

        if (p3x && p3y) {
            ctx.lineTo(p3x, p3y)
            ctx.lineTo(p1x, p1y)
        }

        ctx.stroke()
        ctx.closePath()
        ctx.globalAlpha = this._settings.fillColor.opacity
        ctx.fillStyle = this._settings.fillColor.color
        ctx.fill()
    };

    calculateAttributes() {
        const { p1, p2, p3 } = this._state
        if (!p1 || !p2) return

        let p1x = this.timeToCoordinate(p1.x)
        let p2x = this.timeToCoordinate(p2.x)
        let p1y = this.priceToCoordinate(p1.y)
        let p2y = this.priceToCoordinate(p2.y)
        if (p1x && p2x && p1y && p2y) {
            this.scaledCoordinates.p1x = scaledCoordinate(p1x, this.horizontalPixelRatio) as Coordinate
            this.scaledCoordinates.p1y = scaledCoordinate(p1y, this.verticalPixelRatio) as Coordinate
            this.scaledCoordinates.p2x = scaledCoordinate(p2x, this.horizontalPixelRatio) as Coordinate
            this.scaledCoordinates.p2y = scaledCoordinate(p2y, this.verticalPixelRatio) as Coordinate
        }

        if (!p3) return

        let p3x = this.timeToCoordinate(p3.x)
        let p3y = this.priceToCoordinate(p3.y)

        if (p3x && p3y) {
            this.scaledCoordinates.p3x = scaledCoordinate(p3x, this.horizontalPixelRatio) as Coordinate
            this.scaledCoordinates.p3y = scaledCoordinate(p3y, this.verticalPixelRatio) as Coordinate
        }

        this.calculateArea()
    }

    calculateArea() {
        const { p1x, p1y, p2x, p2y, p3x, p3y } = this.scaledCoordinates
        this.area = calculateTriangleArea(p1x, p1y, p2x, p2y, p3x, p3y)
    }

    onAddPoint = (time: Time, price: number, coordinates: PointCoordinates, preview?: boolean) => {

        const point: Point = {
            id: 'p1',
            x: time,
            y: price
        }
        if (!this.lastAddedPoint) {
            this._state.p1 = point
        } else if (this.lastAddedPoint.id == 'p1') {
            point.id = 'p2'
            this._state.p2 = point
        } else if (this.lastAddedPoint.id == 'p2') {
            point.id = 'p3'
            point.x = time
            point.y = price
            this._state.p3 = point

            !preview && this.onDrawingEnd()
        }

        if (!preview) return { ...point }
    }

    getHandles = (): Point[] => {
        const handles: Point[] = []
        const { p1, p2, p3 } = this._state

        p1 && handles.push(p1)
        p2 && handles.push(p2)
        p3 && handles.push(p3)

        return handles
    }

    hitTest(x: number, y: number): PrimitiveHoveredItem | null {
        const scaledX = scaledCoordinate(x, this.horizontalPixelRatio)
        const scaledY = scaledCoordinate(y, this.verticalPixelRatio)
        const { p1x, p1y, p2x, p2y, p3x, p3y } = this.scaledCoordinates
        const area1 = calculateTriangleArea(p1x, p1y, p2x, p2y, scaledX, scaledY)
        const area2 = calculateTriangleArea(p2x, p2y, p3x, p3y, scaledX, scaledY)
        const area3 = calculateTriangleArea(p1x, p1y, p3x, p3y, scaledX, scaledY)

        if (Number(this.area.toFixed(1)) === Number((area1 + area2 + area3).toFixed(1))) {
            return {
                externalId: this.id,
                zOrder: 'normal'
            }
        }

        return null
    }

    updateHandles(points: Point[]) {
        points.forEach((h) => {
            switch (h.id) {
                case 'p1':
                    this._state.p1 = h
                    break
                case 'p2':
                    this._state.p2 = h
                    break
                case 'p3':
                    this._state.p3 = h
                    break
            }
        })
    }

    updateHandle = (id: string, time: Time, price: number) => {
        this._state[id as keyof typeof this._state]!.x = time
        this._state[id as keyof typeof this._state]!.y = price
    }

    getPriceAxisLabelPoints = (): LabelAxisViewConfig<number>[] => {

        const { p1, p2, p3 } = this._state
        if (!p1 || !p2) return []

        if (!p3) {
            const min = Math.min(p1.y, p2.y)
            const max = Math.max(p1.y, p2.y)

            return [{
                id: 'price',
                p1: min,
                p2: max,
                fill: true,
                fillColor: this._settings.fillColor.color,
                labelColors: {
                    p1: this._settings.labelColor.color,
                    p2: this._settings.labelColor.color
                }
            }]
        } else {

            const sorted = [p1, p2, p3].sort((a, b) => a.y - b.y)
            return [{
                id: 'outer',
                p1: sorted[0].y,
                p2: sorted[2].y,
                fill: true,
                fillColor: this._settings.fillColor.color,
                labelColors: {
                    p1: this._settings.labelColor.color,
                    p2: this._settings.labelColor.color
                }
            }, {
                id: 'inner',
                p1: sorted[1].y,
                p2: sorted[1].y,
                fill: false,
                fillColor: this._settings.fillColor.color,
                labelColors: {
                    p1: this._settings.labelColor.color,
                    p2: this._settings.labelColor.color
                }
            }]
        }

    }

    getTimeAxisLabelPoints = (): LabelAxisViewConfig<Time>[] => {
        const { p1, p2, p3 } = this._state
        if (!p1 || !p2) return []

        if (!p3) {
            const min = Math.min(p1.x as number, p2.x as number)
            const max = Math.max(p1.x as number, p2.x as number)

            return [{
                id: 'time',
                p1: min as Time,
                p2: max as Time,
                fill: true,
                fillColor: this._settings.fillColor.color,
                labelColors: {
                    p1: this._settings.labelColor.color,
                    p2: this._settings.labelColor.color
                }
            }]
        } else {

            const sorted = [p1, p2, p3].sort((a, b) => (a.x as number) - (b.x as number))
            return [{
                id: 'time-outer',
                p1: sorted[0].x,
                p2: sorted[2].x,
                fill: true,
                fillColor: this._settings.fillColor.color,
                labelColors: {
                    p1: this._settings.labelColor.color,
                    p2: this._settings.labelColor.color
                }
            }, {
                id: 'time-inner',
                p1: sorted[1].x,
                p2: sorted[1].x,
                fill: false,
                fillColor: this._settings.fillColor.color,
                labelColors: {
                    p1: this._settings.labelColor.color,
                    p2: this._settings.labelColor.color
                }
            }]
        }
    }


    getAvailableSettings = (): DrawingSettingGroup<TriangleDrawingSettings, SettingValues>[] => {
        return [{
            id: 'style',
            displayName: 'Style',
            visible: true,
            settings: [
                {
                    name: 'fillColor',
                    label: 'Fill Color',
                    type: SettingType.COLOR,
                    defaultValue: {
                        color: '#089981',
                        opacity: 0.2
                    },
                    custom: true,
                    visible: true
                },
                {
                    name: 'labelColor',
                    label: 'Label Color',
                    type: SettingType.COLOR,
                    defaultValue: {
                        color: primaryDrawingColor,
                        opacity: 1
                    },
                    custom: true,
                    visible: true
                },
                {
                    name: 'lineColor',
                    label: 'Border Color',
                    type: SettingType.COLOR,
                    defaultValue: { color: '#089981', opacity: 1 },
                    custom: true,
                    visible: true
                },
                {
                    name: 'lineWidth',
                    label: 'Border Width',
                    type: SettingType.STROKE_WIDTH,
                    defaultValue: 1,
                    custom: true,
                    visible: true
                }
            ]
        }]
    }

    getSettingsValues(): TriangleDrawingSettings {
        return { ...this._settings }
    }

    getMiniSettings = () => {
        const settings = this.getAvailableSettings()
        let miniSettings: (keyof TriangleDrawingSettings)[] = []

        settings.forEach((s) => {
            if (s.id === 'style') {
                miniSettings = [...s.settings.map(setting => setting.name).filter(v => v !== 'labelColor')]
            }
        })

        return miniSettings
    }

    getDrawingType(): DrawingTypes {
        return 'triangle'
    }
}

export default TriangleDrawingTool